import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Assert } from '@cotera/utilities';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import {
  RESOURCE_LIST_FETCH_STALE_MS,
  queryKeys,
} from '@cotera/client/app/hooks/query-cache-keys';
import {
  buildChatSummary,
  folderChatSummaryQuery,
  folderItemTypesForFilter,
  type FolderChatSummary,
  type FolderContentItem,
  type ListParamsState,
} from './folder-contents-page.model';

/**
 * Page size for an expanded folder's children. Inline expansion is a discovery
 * affordance, not an exhaustive browser: a folder with more children than this
 * reports the remainder on its row (see `hiddenChildCounts`) and the folder page
 * — which paginates properly — is where the rest is reachable.
 */
const CHILDREN_LIMIT = 100;

export type FolderChildrenState = {
  childrenByFolderId: ReadonlyMap<string, FolderContentItem[]>;
  /**
   * Server-reported child count per expanded folder, from the same query as the
   * children — so it is already narrowed by the active type filter and search,
   * unlike the folder's own `childrenCount`. Exceeds the fetched slice when a
   * folder holds more than `CHILDREN_LIMIT`, which is how the row knows the
   * disclosed rows are a prefix rather than the whole folder.
   */
  childTotalsByFolderId: ReadonlyMap<string, number>;
  /**
   * Per-folder aggregate behind the collapsed "Chats" row, keyed by folder id.
   * Chats are excluded from the children query (they are excluded from every
   * folder-contents fetch — COT-6561), so without this an expanded folder
   * disclosed its artifacts and silently dropped its chats, which the same
   * folder shows as a "Chats" row the moment you open it as a page.
   *
   * Only present for folders that have finished loading — a folder publishes its
   * children and its summary together, so the disclosed rows are never missing
   * the Chats row that belongs among them.
   */
  chatSummaryByFolderId: ReadonlyMap<string, FolderChatSummary>;
  loadingFolderIds: ReadonlySet<string>;
};

/**
 * Fetches children for each currently-expanded folder.
 *
 * One query per folder (via `useQueries`) rather than a single combined
 * request, so each folder's children cache and invalidate independently and
 * re-expanding a folder is instant. Keys match the shared folder-contents keys,
 * so opening a folder as a page warms these and vice versa.
 */
export function useFolderChildren(
  expandedFolderIds: readonly string[],
  typeFilter: ListParamsState['type'],
  scope: ListParamsState['scope'],
  /** Active sort, so children order matches their parents rather than the server default. */
  sort: ListParamsState['sort'],
  sortDir: ListParamsState['sortDir'],
  /**
   * Active search. Search is resolved server-side for every view (the list's
   * `filterFn` is a no-op), so children must carry it too — otherwise an
   * expanded folder renders its *unfiltered* contents underneath filtered
   * results, and cached children leak across different searches.
   */
  search: ListParamsState['search']
): FolderChildrenState {
  const client = useTenantedClient();
  const orgId = useWhoami((s) => s.org.id);
  const itemTypes = useMemo(
    () => folderItemTypesForFilter(typeFilter),
    [typeFilter]
  );

  const searchParam = search !== '' ? search : undefined;

  const results = useQueries({
    queries: expandedFolderIds.map((folderId) => ({
      queryKey: queryKeys.resource.folderContents({
        orgId,
        folderId,
        ...(scope !== undefined && { folderScope: scope }),
        itemTypes,
        search: searchParam,
        sort,
        sortDir,
        limit: CHILDREN_LIMIT,
        offset: 0,
      }),
      staleTime: RESOURCE_LIST_FETCH_STALE_MS,
      queryFn: async () =>
        Assert.assertOk(
          await client.resource.folder.getFolderItems({
            folderId,
            folderScope: scope,
            itemTypes,
            search: searchParam,
            sort,
            sortDir,
            limit: CHILDREN_LIMIT,
            offset: 0,
          })
        ),
    })),
  });

  // The collapsed "Chats" row belongs to the unfiltered view only — under a type
  // filter it would be the one row ignoring the filter. Matches
  // `fetchFolderContentsForFolder`'s `includeChatSummary`.
  const includeChatSummary = typeFilter === 'all';

  // One chat lookup per expanded folder, through the same spec the folder page
  // uses — so an expanded folder and that folder's own page can't disagree on
  // the count, and either one warms the other's cache.
  const chatSummaryResults = useQueries({
    queries: (includeChatSummary ? expandedFolderIds : []).map((folderId) => {
      const { queryKey, request } = folderChatSummaryQuery({
        orgId,
        folderId,
        scope,
        search: searchParam,
      });
      return {
        queryKey,
        staleTime: RESOURCE_LIST_FETCH_STALE_MS,
        queryFn: async () =>
          Assert.assertOk(await client.resource.folder.getFolderItems(request)),
      };
    }),
  });

  // `useQueries` returns a fresh array every render, so memoizing on it directly
  // would never hit. A dependency array cannot vary in length either, so key the
  // memo on a signature of what actually changes: which folders are expanded,
  // and each query's pending state and last update.
  const signature = expandedFolderIds
    .map((folderId, index) => {
      const result = results[index];
      const chatResult = chatSummaryResults[index];
      return `${folderId}:${result?.isPending === true ? 'p' : 'r'}:${
        result?.dataUpdatedAt ?? 0
      }:${chatResult?.isPending === true ? 'p' : 'r'}:${
        chatResult?.dataUpdatedAt ?? 0
      }`;
    })
    .join('|');
  // Sort and search are part of the query key, so a change produces new queries
  // and a new `dataUpdatedAt` — but include them explicitly so the memo can't
  // serve a stale map in the window before the refetch resolves.
  const querySignature = `${sort ?? ''}:${sortDir}:${searchParam ?? ''}:${
    includeChatSummary ? 'chats' : 'no-chats'
  }`;

  return useMemo((): FolderChildrenState => {
    const childrenByFolderId = new Map<string, FolderContentItem[]>();
    const childTotalsByFolderId = new Map<string, number>();
    const chatSummaryByFolderId = new Map<string, FolderChatSummary>();
    const loadingFolderIds = new Set<string>();
    expandedFolderIds.forEach((folderId, index) => {
      const result = results[index];
      if (result === undefined) {
        return;
      }
      const chatResult = chatSummaryResults[index];
      // A folder's two queries race, and the artifacts one usually wins.
      // Publishing children as soon as it lands disclosed the folder as
      // complete-without-chats and then popped the "Chats" row in whenever the
      // summary caught up — and with a slow summary, complete-without-chats is
      // the state you actually sat looking at. So a folder counts as loading
      // until BOTH have settled, and its children publish together, the way the
      // folder page resolves them (`fetchFolderContentsForFolder` awaits both).
      //
      // An errored summary is settled, not pending: that degrades to disclosing
      // the artifacts with no Chats row rather than spinning forever.
      const chatPending = includeChatSummary && chatResult?.isPending !== false;
      if (result.isPending || chatPending) {
        loadingFolderIds.add(folderId);
        return;
      }
      if (chatResult?.data !== undefined) {
        chatSummaryByFolderId.set(folderId, buildChatSummary(chatResult.data));
      }
      if (result.data !== undefined) {
        childrenByFolderId.set(folderId, result.data.items);
        childTotalsByFolderId.set(folderId, result.data.total);
      }
    });
    return {
      childrenByFolderId,
      childTotalsByFolderId,
      chatSummaryByFolderId,
      loadingFolderIds,
    };
    // `signature` stands in for `results`/`expandedFolderIds` (see above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, querySignature]);
}
